import React, { useEffect, useState, useCallback, Suspense, lazy } from 'react';
// Eager, not lazy: a boundary that has to be fetched cannot catch the
// error that stopped the fetch.
import ErrorBoundary from './components/common/ErrorBoundary';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { GoogleOAuthProvider } from '@react-oauth/google';
import { ThemeProvider, useTheme } from './context/ThemeContext';
import MainLayout from './components/layout/MainLayout';
import ScreenShareProvider from './components/screenshare/ScreenShareProvider';
import { isHandheld } from './utils/device';
import ImpersonationBanner, { readImpersonation } from './components/screenshare/ImpersonationBanner';
import { canAccessRoute, getDefaultRoute } from './components/ProtectedRoute';
import { usePagePermissions, canViewPath, clearPagePermissions, API_BASE } from './utils/pagePermissions';
import { clearAllSessions } from './services/session';
import api, { authAPI, clearAuthToken, setAuthToken, scheduleProactiveRefresh } from './services/api';
import { HelpProvider } from './components/help/HelpContext';
import HelpSidebar from './components/help/HelpSidebar';
import './styles/global.css';
import './App.css';

// Auth pages (small, load eagerly for fast first paint)
import RegisterPage from './pages/auth/RegisterPage';
import LoginPage from './pages/auth/LoginPage';
const AuthorizeCardPage = React.lazy(() => import('./pages/AuthorizeCardPage'));
const CardTermsPage = React.lazy(() => import('./pages/CardTermsPage'));

// Everything else: lazy loaded
const PatientsPage = lazy(() => import('./pages/lis/PatientsPage'));
const AssessmentsPage = lazy(() => import('./pages/lis/AssessmentsPage'));
const DuplicatePatientsPage = lazy(() => import('./pages/lis/DuplicatePatientsPage'));
const PortalAccessRequestsPage = lazy(() => import('./pages/lis/PortalAccessRequestsPage'));
const PatientPortal = lazy(() => import('./pages/portal/PatientPortal'));
const RepPortal = lazy(() => import('./pages/rep/RepPortal'));
const CollectApp = lazy(() => import('./pages/collect/CollectApp'));
const OrdersPage = lazy(() => import('./pages/lis/OrdersPage'));
const MailQueuePage = lazy(() => import('./pages/lis/MailQueuePage'));
const CallLogsPage = lazy(() => import('./pages/lis/CallLogsPage'));
const ResultsPage = lazy(() => import('./pages/lis/ResultsPage'));
const RequisitionsPage = lazy(() => import('./pages/lis/RequisitionsPage'));
const DocumentManagement = lazy(() => import('./pages/lis/DocumentManagement'));
const VehicleManagement = lazy(() => import('./pages/lis/VehicleManagement'));
const DispatchCenter = lazy(() => import('./pages/lis/DispatchCenter'));
const FieldVisitLogs = lazy(() => import('./pages/lis/FieldVisitLogs'));
const LabMaintenance = lazy(() => import('./pages/lis/LabMaintenance'));
const SalesCRM = lazy(() => import('./pages/lis/SalesCRM'));
const AIDashboard = lazy(() => import('./pages/lis/AIDashboard'));
const LiveMap = lazy(() => import('./pages/lis/LiveMap'));
const FleetTelematics = lazy(() => import('./pages/lis/FleetTelematics'));
const FleetAnalytics = lazy(() => import('./pages/lis/FleetAnalytics'));
const AccessioningDashboard = lazy(() => import('./pages/lis/AccessioningDashboard'));
const Accessioning = lazy(() => import('./pages/lis/Accessioning'));
const OrderReceiving = lazy(() => import('./pages/lis/OrderReceiving'));
const ProficiencyTesting = lazy(() => import('./pages/lis/ProficiencyTesting'));
const TasksPage = lazy(() => import('./pages/lis/TasksPage'));
const FeeSchedulesPage = lazy(() => import('./pages/rcm/FeeSchedulesPage'));
const MobileTechView = lazy(() => import('./pages/lis/MobileTechView'));
const TechnicianApp = lazy(() => import('./pages/lis/TechnicianApp'));
const FieldSchedule = lazy(() => import('./pages/lis/FieldSchedule'));
const DispatchPickups = lazy(() => import('./pages/lis/DispatchPickups'));
const RecurringSchedule = lazy(() => import('./pages/lis/RecurringSchedule'));
const ReportBuilder = lazy(() => import('./pages/lis/ReportBuilder'));
const KioskMode = lazy(() => import('./pages/lis/KioskMode'));
const Wallboard = lazy(() => import('./pages/wallboard/Wallboard'));
const SpecimenCheckIn = lazy(() => import('./pages/lis/SpecimenCheckIn'));
const CMSManager = lazy(() => import('./pages/lis/CMSManager'));
const PageBuilder = lazy(() => import('./pages/lis/PageBuilder'));
const WebsiteLayout = lazy(() => import('./pages/website/WebsiteLayout'));
const HomePage = lazy(() => import('./pages/website/HomePage'));
const TestCatalogPage = lazy(() => import('./pages/website/TestCatalogPage'));
const ContactPage = lazy(() => import('./pages/website/ContactPage'));
const PrivacyPage = lazy(() => import('./pages/website/PrivacyPage'));
const TermsPage = lazy(() => import('./pages/website/TermsPage'));
const CMSPage = lazy(() => import('./pages/website/CMSPage'));
const PatientDashboard = lazy(() => import('./components/PatientDashboard'));
// /patient exchanges the staff session for a real portal session and renders
// the portal itself, so the tiles and the PIN are the SAME implementation
// rather than a second one that can drift. Falls back to the dashboard.
const PatientRoute = lazy(() => import('./pages/portal/PatientRoute'));
const MessagingDashboard = lazy(() => import('./components/MessagingDashboard'));
const ExecutiveDashboard = lazy(() => import('./components/ExecutiveDashboard'));
const StoreFront = lazy(() => import('./components/ecommerce/StoreFront'));
const CartPage = lazy(() => import('./components/ecommerce/CartPage'));
const CheckoutPage = lazy(() => import('./components/ecommerce/CheckoutPage'));
const OrderConfirmation = lazy(() => import('./components/ecommerce/OrderConfirmation'));
const MyOrders = lazy(() => import('./components/ecommerce/MyOrders'));
const MySubscriptions = lazy(() => import('./components/ecommerce/MySubscriptions'));
const AdminEcommerceDashboard = lazy(() => import('./components/ecommerce/AdminEcommerceDashboard'));
const AdminFulfillment = lazy(() => import('./components/ecommerce/AdminFulfillment'));
const AdminShipments = lazy(() => import('./components/ecommerce/AdminShipments'));
const SubscriptionManager = lazy(() => import('./components/admin/SubscriptionManager'));
const OperationsDashboard = lazy(() => import('./components/OperationsDashboard').then(m => ({ default: m.OperationsDashboard })));
const LabOpsHub = lazy(() => import('./pages/lis/LabOpsHub'));
const PhlebotomyLog = lazy(() => import('./pages/lis/PhlebotomyLog'));
const EodReport = lazy(() => import('./pages/lis/EodReport'));
const EodReportsAdmin = lazy(() => import('./pages/lis/EodReportsAdmin'));
const DepartmentChat = lazy(() => import('./pages/lis/DepartmentChat'));
const TechRoutePage = lazy(() => import('./pages/lis/TechRoutePage'));
const PatientsManager = lazy(() => import('./components/lis/PatientsManager'));
const OrdersManager = lazy(() => import('./components/lis/OrdersManager'));
const ResultsManager = lazy(() => import('./components/lis/ResultsManager'));
const ProvidersManager = lazy(() => import('./components/admin/ProvidersManager'));
const ClaimsManager = lazy(() => import('./components/rcm/ClaimsManager'));
const WorkQueuesPage = lazy(() => import('./components/rcm/WorkQueuesPage'));
const AppealsPage = lazy(() => import('./components/rcm/AppealsPage'));
const AISettingsPage = lazy(() => import('./components/rcm/AISettingsPage'));
const PaperClaimsQueue = lazy(() => import('./components/rcm/PaperClaimsQueue'));
const PayersPage = lazy(() => import('./components/rcm/PayersPage'));
const StatementsManager = lazy(() => import('./components/rcm/StatementsManager'));
const InvoicesManager = lazy(() => import('./components/rcm/InvoicesManager'));
const RCMDashboard = lazy(() => import('./components/rcm/RCMDashboard'));
const EOBManager = lazy(() => import('./components/rcm/EOBManager'));
const RecoupmentsManager = lazy(() => import('./components/rcm/RecoupmentsManager'));
const FaxInboxPage = lazy(() => import('./pages/lis/FaxInboxPage'));
const EmailInboxPage = lazy(() => import('./pages/lis/EmailInboxPage'));

const SpecimensTracker = lazy(() => import('./components/lis/SpecimensTracker'));
const QCManager = lazy(() => import('./components/lis/QCManager'));
const AuditLogsViewer = lazy(() => import('./components/lis/AuditLogsViewer'));
const InventoryItems = lazy(() => import('./components/inventory/InventoryItems'));
const InventoryLots = lazy(() => import('./components/inventory/InventoryLots'));
const PurchaseOrders = lazy(() => import('./components/inventory/PurchaseOrders'));
const InventoryDashboard = lazy(() => import('./components/inventory/InventoryDashboard'));
const FulfillmentManager = lazy(() => import('./components/warehouse/FulfillmentManager'));
const ReorderAlerts = lazy(() => import('./components/warehouse/ReorderAlerts'));
// Tenant-aware inventory hub (Phase 1-5)
const InventoryHub = lazy(() => import('./pages/lis/InventoryHub'));
const InventoryAudit = lazy(() => import('./pages/lis/InventoryAudit'));
const ConsumptionStation = lazy(() => import('./pages/lis/ConsumptionStation'));
const SupplyRequestsPage = lazy(() => import('./pages/lis/SupplyRequestsPage'));
const InventorySettings = lazy(() => import('./pages/lis/InventorySettings'));
const ExecutivePage = lazy(() => import('./pages/lis/ExecutivePage'));
const UserActivityPage = lazy(() => import('./pages/lis/UserActivityPage'));
const PurchasingPage = lazy(() => import('./pages/lis/PurchasingPage'));
const PurchaseOrderDetailPage = lazy(() => import('./pages/lis/PurchaseOrderDetailPage'));
const SchedulePage = lazy(() => import('./components/admin/SchedulePage'));
const ClientManagementPage = lazy(() => import('./components/admin/ClientManagementPage'));
const UserSettings = lazy(() => import('./components/admin/UserSettings'));
const MyAccountPage = lazy(() => import('./pages/MyAccountPage'));
const SettingsPage = lazy(() => import('./pages/SettingsPage'));
const BusinessLogicRules = lazy(() => import('./components/admin/BusinessLogicRules'));
const RoleManagement = lazy(() => import('./components/admin/RoleManagement'));
const LabPortalDashboard = lazy(() => import('./pages/lab/LabDashboard'));

const RCMModule = lazy(() => import('./pages/rcm/RCMModule'));
const PayerPortalsPage = lazy(() => import('./pages/lis/PayerPortalsPage'));
const InterfacesPage = lazy(() => import('./pages/lis/InterfacesPage'));
const MicrobiologyPage = lazy(() => import('./pages/lis/MicrobiologyPage'));
const SendOutsPage = lazy(() => import('./pages/lis/SendOutsPage'));
const HelpCenter = lazy(() => import('./pages/HelpCenter'));
const CommunicationCenter = lazy(() => import('./pages/lis/CommunicationCenter'));
const PublicSignProvider = lazy(() => import('./pages/PublicSignProvider'));
const SigningPage = lazy(() => import('./pages/SigningPage'));
const PatientQuestionnairePage = lazy(() => import('./pages/PatientQuestionnairePage'));
const W9Page = lazy(() => import('./pages/W9Page'));
const OIGScreening = lazy(() => import('./pages/compliance/OIGScreening'));
const CommissionsPage = lazy(() => import('./pages/sales/CommissionsPage'));
const SettlementsPage = lazy(() => import('./pages/rcm/SettlementsPage'));
const OnboardClientWizard = lazy(() => import('./pages/lis/OnboardClientWizard'));
const DispatchAnalytics = lazy(() => import('./pages/lis/DispatchAnalytics'));
const CollectorPerformance = lazy(() => import('./pages/lis/CollectorPerformance'));
const ClientServiceDashboard = lazy(() => import('./pages/lis/ClientServiceDashboard'));
const BillingManagerDashboard = lazy(() => import('./pages/lis/BillingManagerDashboard'));
const BillerDashboard = lazy(() => import('./pages/lis/BillerDashboard'));
const PDFHub = lazy(() => import('./pages/lis/PDFHub'));
const SaasBillingPage = lazy(() => import('./pages/SaasBillingPage'));
const GoogleWorkspacePage = lazy(() => import('./pages/GoogleWorkspacePage'));
const SystemHealth = lazy(() => import('./pages/lis/SystemHealth'));

// Route guard — blocks access based on role
function AccessDeniedPage({ user, path, onLogout }) {
  // Which access level this login carries at its practices, so the page names
  // what was actually assigned rather than the account type.
  const [accessLevels, setAccessLevels] = useState([]);
  useEffect(() => {
    if ((user?.role || '').toLowerCase() !== 'client') return;
    const token = localStorage.getItem('token');
    if (!token) return;
    fetch(`${API_BASE}/page-permissions/my-context`,
          { headers: { Authorization: `Bearer ${token}` } })
      .then(r => (r.ok ? r.json() : null))
      .then(d => setAccessLevels(d?.access_levels || []))
      .catch(() => {});
  }, [user]);
  const [message, setMessage] = React.useState('');
  const [sent, setSent] = React.useState(false);
  const [sending, setSending] = React.useState(false);
  const role = (user?.role || '').toLowerCase();

  // Where "Go to Dashboard" would send them — and whether they can actually
  // get there. If the role's own default route is itself refused, the button
  // returns to this page and there is no way off it except closing the tab.
  // Sign out is offered unconditionally for the same reason: being stuck
  // signed in with nowhere to go is the worst state this page can leave
  // somebody in.
  //
  // It is resolved against the permission map and this person's own landing
  // page, so it is no longer "the Dashboard or nothing" — a role that cannot
  // see the Dashboard is offered whatever it can see instead.
  const { permissions } = usePagePermissions();
  const home = getDefaultRoute(role, {
    isCollector: !!user?.is_mobile_collector,
    defaultPage: user?.default_page_effective || user?.default_page,
    permissions,
  });

  // Where this person can actually go. The old check asked canAccessRoute,
  // which is permissive and answers "yes" for every non-admin role, so the
  // only thing that could make the button disappear was the default route
  // being the very page just refused — and then the screen claimed there was
  // no page this account could open, while eight of them were sitting there.
  const openable = React.useMemo(() => {
    if (!permissions) return [];
    const label = (c) => c.split('/').pop().replace(/-/g, ' ')
      .replace(/\b\w/g, m => m.toUpperCase());
    return Object.keys(permissions)
      .filter(c => permissions[c]?.can_view && `/${c}` !== path)
      .sort()
      .map(c => ({ code: c, to: `/${c}`, label: label(c) }));
  }, [permissions, path]);
  const homeReachable = openable.some(o => o.to === home);
  // Label it with the page it actually opens. It said "Dashboard" whatever it
  // pointed at, which for a role without the Dashboard was a button naming a
  // page they had just been refused.
  const homeLabel = openable.find(o => o.to === home)?.label || 'Dashboard';
  const suggestions = (homeReachable
    ? [{ to: home, label: homeLabel }, ...openable.filter(o => o.to !== home)]
    : openable).slice(0, 4);

  const signOut = async () => {
    try { await onLogout?.(); } catch { /* sign out must not be blockable */ }
    // Every session, not just the staff one. A person can be holding a staff
    // token and a portal token at the same time — that is by design — and
    // clearing one of them is what made signing out look like it had failed.
    try { await clearAllSessions({ revoke: false }); } catch { /* already local */ }
    // A hard navigation, not a router push: the point is to leave whatever
    // state produced this screen behind entirely.
    window.location.assign('/login');
  };

  const handleRequest = async () => {
    setSending(true);
    try {
      const { authAPI } = require('./services/api');
      const api = require('./services/api').default;
      await api.post('/communications/messages/send-email', {
        recipient: 'admin@lis.local',
        subject: `Access Request: ${path}`,
        body: `User: ${user?.full_name || user?.username} (${user?.email})\nRole: ${user?.role}\nRequested Page: ${path}\n\nMessage: ${message || 'No additional message.'}`,
        html_body: `
          <div style="font-family: sans-serif; max-width: 500px;">
            <h2 style="color: #6366f1;">Access Request</h2>
            <table style="font-size: 14px; line-height: 2;">
              <tr><td style="font-weight: 600; padding-right: 16px;">User:</td><td>${user?.full_name || user?.username}</td></tr>
              <tr><td style="font-weight: 600; padding-right: 16px;">Email:</td><td>${user?.email}</td></tr>
              <tr><td style="font-weight: 600; padding-right: 16px;">Current Role:</td><td>${user?.role}</td></tr>
              <tr><td style="font-weight: 600; padding-right: 16px;">Requested Page:</td><td><code>${path}</code></td></tr>
            </table>
            ${message ? `<p style="margin-top: 16px; padding: 12px; background: #f9fafb; border-radius: 8px; font-size: 14px;">${message}</p>` : ''}
          </div>`,
        message_type: 'access_request',
      });
      setSent(true);
    } catch {
      setSent(true); // Show success anyway — request is logged even if email fails
    }
    setSending(false);
  };

  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '70vh' }}>
      <div style={{ maxWidth: 440, width: '100%', textAlign: 'center', padding: 24 }}>
        {/* Icon */}
        <div style={{ width: 72, height: 72, borderRadius: '50%', background: 'rgba(99,102,241,0.08)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 20px', border: '1px solid rgba(99,102,241,0.15)' }}>
          <svg width="32" height="32" viewBox="0 0 24 24" fill="none" stroke="#6366f1" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
            <rect x="3" y="11" width="18" height="11" rx="2" ry="2"/><path d="M7 11V7a5 5 0 0 1 10 0v4"/>
          </svg>
        </div>

        <div style={{ fontSize: 22, fontWeight: 700, color: '#111827', marginBottom: 6 }}>Access Restricted</div>
        <div style={{ fontSize: 14, color: '#6b7280', marginBottom: 6 }}>
          You don't have permission to access <code style={{ background: '#f1f5f9', padding: '2px 6px', borderRadius: 4, fontSize: 13 }}>{path}</code>
        </div>
        <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 24 }}>
          {/* 'client' is the ACCOUNT type. What the person may actually do is
              the access level on their client link — saying "client" to
              someone with Client Orders names the wrong thing. */}
          Your current role: <span style={{ fontWeight: 600, color: '#6366f1' }}>
            {user?.role}{accessLevels.length ? ` · ${accessLevels.join(', ')} access` : ''}
          </span>
        </div>

        {!sent ? (
          <div style={{ background: 'rgba(255,255,255,0.8)', backdropFilter: 'blur(12px)', border: '1px solid rgba(99,102,241,0.15)', borderRadius: 14, padding: 20, textAlign: 'left', marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#111827', marginBottom: 10 }}>Request Access</div>
            <div style={{ fontSize: 12, color: '#6b7280', marginBottom: 12 }}>Send a request to the administrator to grant you access to this page.</div>
            <textarea
              value={message}
              onChange={e => setMessage(e.target.value)}
              placeholder="Why do you need access? (optional)"
              rows={3}
              style={{ width: '100%', padding: '8px 12px', border: '1px solid rgba(99,102,241,0.2)', borderRadius: 8, fontSize: 13, resize: 'vertical', outline: 'none', background: 'rgba(255,255,255,0.7)', marginBottom: 12 }}
            />
            <div style={{ display: 'flex', gap: 8 }}>
              <button onClick={handleRequest} disabled={sending}
                style={{ flex: 1, padding: '10px 16px', border: '1px solid rgba(99,102,241,0.3)', borderRadius: 10, background: 'rgba(99,102,241,0.08)', color: '#6366f1', cursor: 'pointer', fontSize: 13, fontWeight: 700, transition: 'all 0.15s', opacity: sending ? 0.6 : 1 }}
                onMouseEnter={e => { if (!sending) e.currentTarget.style.background = 'rgba(99,102,241,0.15)'; }}
                onMouseLeave={e => { e.currentTarget.style.background = 'rgba(99,102,241,0.08)'; }}>
                {sending ? 'Sending...' : 'Request Access & Go Home'}
              </button>
            </div>
          </div>
        ) : (
          <div style={{ background: 'rgba(16,185,129,0.06)', border: '1px solid rgba(16,185,129,0.2)', borderRadius: 14, padding: 20, marginBottom: 16 }}>
            <div style={{ fontSize: 14, fontWeight: 700, color: '#059669', marginBottom: 4 }}>Request Sent</div>
            <div style={{ fontSize: 12, color: '#6b7280' }}>An administrator will review your request and update your access.</div>
          </div>
        )}

        {suggestions.length > 0 && (
          <div style={{ marginBottom: 14 }}>
            <div style={{ fontSize: 12, color: '#94a3b8', marginBottom: 8 }}>
              Pages you can open:
            </div>
            <div style={{ display: 'flex', gap: 8, justifyContent: 'center', flexWrap: 'wrap' }}>
              {suggestions.map(sgn => (
                <a key={sgn.to} href={sgn.to}
                  style={{ padding: '8px 16px', border: '1px solid rgba(99,102,241,0.3)',
                           borderRadius: 10, background: 'rgba(99,102,241,0.08)',
                           color: '#6366f1', textDecoration: 'none',
                           fontSize: 13, fontWeight: 600 }}>
                  {sgn.label}
                </a>
              ))}
            </div>
          </div>
        )}

        <div style={{ display: 'flex', gap: 10, justifyContent: 'center', flexWrap: 'wrap' }}>
          {homeReachable ? (
            <a href={home}
              style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 24px', border: '1px solid #e5e7eb', borderRadius: 10, background: '#fff', color: '#374151', textDecoration: 'none', fontSize: 13, fontWeight: 600, transition: 'all 0.15s' }}
              onMouseEnter={e => { e.currentTarget.style.background = '#f9fafb'; }}
              onMouseLeave={e => { e.currentTarget.style.background = '#fff'; }}>
              ← Go to Dashboard
            </a>
          ) : null}
          <button onClick={signOut}
            style={{ display: 'inline-flex', alignItems: 'center', gap: 6, padding: '10px 24px', border: '1px solid rgba(220,38,38,0.35)', borderRadius: 10, background: 'transparent', color: '#dc2626', cursor: 'pointer', fontSize: 13, fontWeight: 700 }}>
            Sign out
          </button>
        </div>
        {permissions && openable.length === 0 ? (
          /* Only when it is actually true. It used to appear whenever the
             default route happened to be the refused page, telling people
             with plenty of access that they had none. */
          <div style={{ fontSize: 12, color: '#94a3b8', marginTop: 12, maxWidth: 380, marginLeft: 'auto', marginRight: 'auto' }}>
            There is no page this account can open. Sign out and use the login
            your laboratory gave you — a patient signs in at <code>/portal</code>,
            a sales rep at <code>/rep</code>.
          </div>
        ) : null}
      </div>
    </div>
  );
}

function RouteGuard({ user, children, onLogout }) {
  const location = useLocation();
  const role = (user?.role || '').toLowerCase();
  // The role check is deliberately permissive and only blocks the few
  // admin-only paths. The assigned page permissions are the real answer, and
  // until now nothing consulted them on navigation — the sidebar hid the link
  // and the URL still worked.
  const { permissions } = usePagePermissions();
  if (!canAccessRoute(role, location.pathname)
      || !canViewPath(permissions, role, location.pathname)) {
    return <AccessDeniedPage user={user} path={location.pathname} onLogout={onLogout} />;
  }
  return children;
}

/**
 * Home — wherever this person can actually start.
 *
 * The catch-all route used to `Navigate` to a hardcoded '/dashboard' for
 * every role that was not a patient, which is what made the Dashboard page
 * compulsory: untick View on it and the role lands on it anyway, and is
 * refused by the guard. Every client role in the matrix was in that state.
 *
 * It waits for the permission map rather than guessing: redirecting first and
 * correcting afterwards would flash Access Restricted on the way past.
 */
function HomeRedirect({ user }) {
  const { permissions, loading } = usePagePermissions();
  if (loading) return <RouteLoader />;
  const to = getDefaultRoute((user?.role || '').toLowerCase(), {
    isCollector: !!user?.is_mobile_collector,
    defaultPage: user?.default_page_effective || user?.default_page,
    permissions,
  });
  return <Navigate replace to={to} />;
}

// Loading spinner for lazy routes
function RouteLoader() {
  return (
    <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '60vh', gap: 12 }}>
      <div style={{ width: 28, height: 28, border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.6s linear infinite' }} />
      <span style={{ color: '#94a3b8', fontSize: 14, fontWeight: 500 }}>Loading...</span>
    </div>
  );
}

const normalizeRole = (role) => {
  if (!role) return '';
  return String(role).toLowerCase();
};


/**
 * Persist the signed-in profile alongside React state.
 *
 * Several screens decide what to show from
 * `JSON.parse(localStorage.getItem('user')).role` — OrdersPage,
 * InstrumentManagerManuals, FleetAnalytics, the supply-request screens. Nothing
 * ever wrote that key: the login stores token, lab_id, user_id and username,
 * and the profile lived only in App state. So every one of those checks read
 * undefined and silently took the staff branch — which is how a client login
 * was still being shown warehouse controls after they were supposedly gated.
 */
function persistUser(u) {
  try {
    if (u) localStorage.setItem('user', JSON.stringify(u));
    else localStorage.removeItem('user');
  } catch (e) { /* private mode / quota — the app still works from state */ }
  // Page permissions are cached per session. Whoever signs in next must not
  // inherit the last person's map — sign out and back in as a client in the
  // same tab and it would still be holding the admin's answer.
  clearPagePermissions();
}

/**
 * The browser tab belongs to whoever's domain this is.
 *
 * index.html ships a neutral title because one shell is served on every
 * customer's domain — naming a tenant there put that brand in every other
 * tenant's tab. /domains/current resolves the host to its own company, so the
 * title is set from that once, on boot.
 */
function TenantTitle() {
  React.useEffect(() => {
    let cancelled = false;
    api.get('/domains/current')
      .then(r => {
        if (cancelled) return;
        const co = r.data?.company;
        const name = co?.dba_name || co?.legal_name;
        if (r.data?.platform) document.title = 'Xpiva — Patient Centric System';
        else if (name) document.title = name;
      })
      .catch(() => { /* leave the neutral title rather than guess */ });
    return () => { cancelled = true; };
  }, []);
  return null;
}

/** Leave the SPA entirely. `<Navigate>` would render the stored copy. */
function HardRedirect({ to }) {
  React.useEffect(() => { window.location.replace(to); }, [to]);
  return null;
}

function App() {
  const [user, setUser] = useState(null);
  const [loading, setLoading] = useState(true);
  const [bootstrapError, setBootstrapError] = useState(false);
  const { dark, toggleTheme } = useTheme();
  const [themePicker, setThemePicker] = useState(false);

  // Auth bootstrap. A network/timeout failure here must NEVER leave the app on
  // an infinite spinner (the lock-pileup incident): we time the call out at 20s
  // and surface a "Reconnecting / Retry" screen instead of hanging forever. A
  // real 401 (expired token) is treated as logged-out, not an error.
  const bootstrap = useCallback(() => {
    const token = localStorage.getItem('token');
    if (!token) {
      setLoading(false);
      return;
    }
    setLoading(true);
    setBootstrapError(false);
    setAuthToken(token);
    authAPI.getCurrentUser({ timeout: 20000 })
      .then(res => { setUser(res.data); persistUser(res.data); setBootstrapError(false); })
      .catch(err => {
        if (err?.response?.status === 401) {
          // Token genuinely invalid/expired → drop it and show login.
          clearAuthToken();
          localStorage.removeItem('token');
          setBootstrapError(false);
        } else {
          // Timeout / network / 5xx — keep the token, let the user retry.
          setBootstrapError(true);
        }
      })
      .finally(() => setLoading(false));
    // Pre-warm the lab timezone cache so the first render of any timestamp
    // uses Pacific time (or whatever the lab is configured for) instead of
    // falling back to the browser's zone.
    import('./utils/labTime').then(m => m.prefetchLabTimezone?.());
  }, []);

  useEffect(() => { bootstrap(); }, [bootstrap]);

  // Auto-logout on 401 (token expired/invalid)
  useEffect(() => {
    const handleUnauthorized = () => {
      localStorage.removeItem('token');
      clearAuthToken();
      setUser(null); persistUser(null);
    };
    window.addEventListener('auth:unauthorized', handleUnauthorized);
    return () => window.removeEventListener('auth:unauthorized', handleUnauthorized);
  }, []);

  /* THE SESSION ENDS WHEN THE TOKEN DOES. ONE POLICY, NOT TWO.
   *
   *   "i need you to investigate why in the middle of my work you logged
   *    vincenzo@xpiva.com [out], i told you to let a user stay logged in for
   *    20 hours"
   *
   * This was a SECOND, HIDDEN inactivity policy: a hardcoded
   * `TIMEOUT = 30 * 60 * 1000` that wiped the token and signed the user out
   * after thirty minutes without a mousedown, keydown, scroll or touch —
   * regardless of anything the server said. Raising the server's window to
   * twenty hours did nothing to it, because it never asked the server.
   *
   * The logout that prompted this is in the access log to the second: last
   * interaction 05:15:05 (a label print), browser dumped to the public site
   * at 05:45:05. Thirty minutes exactly.
   *
   * It also could not be found by searching for what it does — there is no
   * "logout", "idle" or "inactivity" identifier anywhere in it, which is why
   * an earlier sweep for exactly those words reported that no client-side
   * timer existed. It is named here now.
   *
   * What replaces it: the token's OWN expiry, which is the single source of
   * truth and the thing the keepalive already renews while somebody works.
   * Stepping away from the keyboard no longer ends a session — being away
   * long enough for the token to lapse does, which is what a twenty-hour
   * session means. Polling re-reads the stored token every minute, so a
   * renewal issued by the keepalive extends this automatically rather than
   * racing it.
   */
  useEffect(() => {
    if (!user) return;
    const expiryOf = (tok) => {
      try {
        const p = JSON.parse(atob(String(tok).split('.')[1]));
        return p?.exp ? p.exp * 1000 : null;
      } catch { return null; }
    };
    const tick = () => {
      const tok = localStorage.getItem('token');
      if (!tok) return;                    // signed out elsewhere; nothing to do
      const exp = expiryOf(tok);
      // A token we cannot read is NOT treated as expired: the server is the
      // judge of that, and throwing away a session over a parse failure is
      // the behaviour this whole change exists to remove.
      if (exp == null) return;
      if (exp > Date.now()) return;
      localStorage.removeItem('token');
      clearAuthToken();
      setUser(null); persistUser(null);
    };
    const timer = setInterval(tick, 60 * 1000);
    tick();
    return () => clearInterval(timer);
  }, [user]);

  // ── Welcome popup state ──
  const [welcomePopup, setWelcomePopup] = useState(null);

  const MEDICAL_JOKES = [
    ["Why did the lab tech break up with the microscope?", "They just couldn't see things the same way."],
    ["What did the blood sample say to the centrifuge?", "You spin me right round, baby!"],
    ["Why don't phlebotomists ever get lost?", "They always follow the veins."],
    ["What's a lab tech's favorite type of music?", "Heavy metal... panels."],
    ["Why was the lab report feeling down?", "It had too many critical values."],
    ["What did the doctor say to the lab?", "I need results, STAT — but no pressure!"],
    ["Why did the specimen cup go to therapy?", "It was tired of everyone's crap."],
    ["What did one test tube say to the other?", "Stop being so negative — you're not even type O."],
    ["Why do lab techs love their jobs?", "Because every day is a new culture experience."],
    ["What's a phlebotomist's pickup line?", "I feel a strong vein connection between us."],
    ["Why did the patient bring a ladder to the lab?", "The doctor ordered a high panel."],
    ["What did the red blood cell say at retirement?", "It's been a long circulation."],
    ["Why was the white blood cell always invited to parties?", "Because it really knew how to fight off bad vibes."],
    ["What do you call a fake lab result?", "A specimen of imagination."],
    ["Why did the bacteria fail the exam?", "It kept multiplying instead of dividing."],
    ["Why did the platelet go to school?", "To stop the bleeding-edge technology gap."],
    ["What do you call a lab tech who works overnight?", "A culture vulture."],
    ["Why did the hemoglobin break up with oxygen?", "It felt too attached."],
    ["What did the pathologist say at the comedy show?", "This is killing me... wait, I see that every day."],
    ["Why did the patient refuse the blood test?", "They didn't want anyone getting under their skin."],
    ["What's a lab's favorite holiday?", "Valen-TINES day — because of all the blood draws."],
    ["Why was the centrifuge a great DJ?", "It always had the best spins."],
    ["What did the glucose meter say to the patient?", "You're looking sweet today."],
    ["Why do urinalysis techs make great detectives?", "They always find the evidence."],
    ["What did the agar plate say to the bacteria?", "You've really grown on me."],
    ["Why was the lab coat always calm?", "It had everything under control... and culture."],
    ["Why did the specimen go to the party?", "Because it was a positive type."],
    ["What do doctors and lab techs have in common?", "Neither can read each other's handwriting."],
    ["Why did the MRI machine break up with the CT scanner?", "There was no chemistry — just imaging."],
    ["What did the lab say on Friday afternoon?", "Let's wrap this up — the incubator can handle the weekend."],
    ["Why did the doctor carry a red pen?", "In case they needed to draw blood."],
    ["What's a hematologist's favorite game?", "Clue — it's always about the blood."],
    ["Why was the lab tech always early?", "Because they couldn't resist a good morning run... of controls."],
    ["What did the QC result say to the patient sample?", "Hold on, let me go first."],
    ["Why did the insurance claim go to the bar?", "It needed to get processed."],
    ["What's a clearinghouse's favorite dance?", "The 837-step."],
    ["Why did the requisition form feel important?", "Because without it, nothing gets ordered."],
    ["What do you call a physician who can't stop ordering tests?", "A lab-aholic."],
    ["Why was the CBC so popular?", "It always had a complete story to tell."],
    ["What did the patient say after their 10th blood draw?", "At this point, just give me a loyalty card."],
  ];

  const getGreeting = () => {
    const h = new Date().getHours();
    if (h < 12) return 'Good Morning';
    if (h < 17) return 'Good Afternoon';
    return 'Good Evening';
  };

  const showWelcome = useCallback((userData) => {
    const showJokes = localStorage.getItem('showJokes') !== 'false'; // default ON
    let setup = null, punchline = null;
    if (showJokes) {
      const j = MEDICAL_JOKES[Math.floor(Math.random() * MEDICAL_JOKES.length)];
      setup = j[0]; punchline = j[1];
    }
    const name = userData?.full_name || userData?.username || 'there';
    const firstName = name.split(' ')[0];
    setWelcomePopup({ greeting: getGreeting(), name: firstName, setup, punchline });
    // First-login theme picker: shown once, then never again.
    if (!localStorage.getItem('themePromptDismissed')) {
      setThemePicker(true);
    }
    // No auto-dismiss — user clicks "Let's Go!" to close
  }, []);

  const pickTheme = useCallback((wantDark) => {
    if (wantDark !== dark) toggleTheme();
    localStorage.setItem('themePromptDismissed', '1');
    setThemePicker(false);
  }, [dark, toggleTheme]);

  // usernameOrToken can be: (a) a username + password, (b) a Google token (isGoogleToken=true),
  // or (c) a JWT access token already obtained (e.g. from 2FA verify or plain login response).
  // When (c), the second arg may be the refresh_token — detect by shape (JWTs contain 2 dots).
  const handleLogin = async (usernameOrToken, passwordOrRefresh, isGoogleToken) => {
    try {
      let token;
      let refreshToken;
      const looksLikeJwt = typeof usernameOrToken === 'string'
        && usernameOrToken.length > 40
        && usernameOrToken.split('.').length === 3;
      if (isGoogleToken) {
        const res = await authAPI.googleLogin(usernameOrToken);
        token = res.data.access_token;
        refreshToken = res.data.refresh_token;
      } else if (looksLikeJwt) {
        // Direct token handoff (from 2FA verify or post-login response).
        token = usernameOrToken;
        refreshToken = (typeof passwordOrRefresh === 'string' && passwordOrRefresh.length > 20)
          ? passwordOrRefresh : null;
      } else if (passwordOrRefresh) {
        const res = await authAPI.login(usernameOrToken, passwordOrRefresh);
        token = res.data.access_token;
        refreshToken = res.data.refresh_token;
      } else {
        token = usernameOrToken;
      }
      localStorage.setItem('token', token);
      if (refreshToken) localStorage.setItem('refresh_token', refreshToken);
      setAuthToken(token, refreshToken);
      try { scheduleProactiveRefresh(token); } catch {}
      const profileRes = await authAPI.getCurrentUser();
      setUser(profileRes.data);
      persistUser(profileRes.data);
      showWelcome(profileRes.data);
      // If the admin set a default landing page for this user, navigate
      // there. The router only listens to in-app navigation, so we use
      // a hard redirect to ensure the new path is honored from a fresh
      // mount (and bypasses any /home auto-redirect).
      try {
        // The effective one: this user's own page if they have one, else the
        // page set for their role on the Roles & Permissions screen. The
        // server resolves it — and refuses to return a page the role cannot
        // view — so the two cannot drift apart here.
        // A collector lands on the collector app, whatever else is set.
        // Checked BEFORE default_page: an administrator who set this person
        // a landing page before they became a collector would otherwise keep
        // dropping them somewhere they have no reason to be.
        // Same rule as getDefaultRoute: the collector app is where a
        // collector lands ON A HAND-HELD. On a PC or a Mac they get the full
        // application, which is what the screen is for.
        const dp = (profileRes.data?.is_mobile_collector && isHandheld())
          ? '/collect'
          : (profileRes.data?.default_page_effective
             || profileRes.data?.default_page);
        if (dp && typeof dp === 'string' && dp.startsWith('/')) {
          // Skip if we're already there to avoid an unnecessary reload.
          if (window.location.pathname !== dp) {
            window.location.replace(dp);
            return;
          }
        }
      } catch {}
    } catch (err) {
      console.error('Login error:', err);
      throw err;
    }
  };

  const handleLogout = async () => {
    // Fire-and-forget: revoke the refresh token server-side so it can't be reused.
    const refreshToken = localStorage.getItem('refresh_token');
    if (refreshToken) {
      try {
        const api = (await import('./services/api')).default;
        api.post('/auth/logout', { refresh_token: refreshToken }).catch(() => {});
      } catch {}
    }
    clearAuthToken();
    // The portal and rep tokens live in sessionStorage under their own keys,
    // so clearing the staff token alone left them behind — and the next visit
    // to /portal or /rep walked straight back in on a session the person
    // thought they had ended.
    try { sessionStorage.removeItem('portal_token'); } catch {}
    try { sessionStorage.removeItem('rep_token'); } catch {}
    setUser(null); persistUser(null);
    // Land on the sign-in screen rather than wherever the guard happens to
    // send a signed-out user — the same destination the portal and the rep
    // portal now use.
    window.location.replace('/login');
  };

  if (loading) {
    return (
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', height: '100vh' }}>
        <div style={{ width: 40, height: 40, border: '3px solid #e2e8f0', borderTopColor: '#6366f1', borderRadius: '50%', animation: 'spin 0.8s linear infinite' }} />
      </div>
    );
  }

  // Bootstrap failed (timeout / network / server) — never leave an infinite
  // spinner. Give the user a clear status and a way to retry.
  if (bootstrapError) {
    return (
      <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '100vh', gap: 16, padding: 24, textAlign: 'center' }}>
        <div style={{ fontSize: 16, fontWeight: 600 }}>Reconnecting…</div>
        <div style={{ fontSize: 13, opacity: 0.7, maxWidth: 420 }}>
          We couldn't reach the server. This is usually temporary — please check your
          connection and try again.
        </div>
        <div style={{ display: 'flex', gap: 10 }}>
          <button
            onClick={bootstrap}
            style={{ padding: '8px 18px', borderRadius: 8, border: 'none', background: '#6366f1', color: '#fff', fontWeight: 600, cursor: 'pointer' }}
          >
            Retry
          </button>
          <button
            onClick={() => { clearAuthToken(); localStorage.removeItem('token'); setBootstrapError(false); }}
            style={{ padding: '8px 18px', borderRadius: 8, border: '1px solid #cbd5e1', background: 'transparent', color: 'inherit', cursor: 'pointer' }}
          >
            Sign in again
          </button>
        </div>
      </div>
    );
  }

  const role = normalizeRole(user?.role);
  // No `defaultRoute` constant here. One used to sit at this line reading
  // `role === 'patient' ? '/patient' : '/dashboard'`, left behind when the
  // catch-all stopped hardcoding Dashboard. Nothing referenced it, and an
  // unused constant that looks like the answer is how the bug comes back.
  // Where somebody starts is HomeRedirect's job, from the permission map.

  const googleClientId = process.env.REACT_APP_GOOGLE_CLIENT_ID || '';
  const oauthWrapper = (children) => googleClientId
    ? <GoogleOAuthProvider clientId={googleClientId}>{children}</GoogleOAuthProvider>
    : children;

  if (!user) {
    return oauthWrapper(
      <Router>
        <Routes>
          <Route path="/register" element={<RegisterPage />} />
          <Route path="/login" element={<LoginPage onLogin={handleLogin} />} />
          {/* A CARDHOLDER IS NOT A USER. These two arrive by text message
              on somebody's phone — they have no account here and must
              never be asked to make one in order to agree to something, or
              to read what they agreed to. The token is the only
              credential, it expires, and it reveals no card number. */}
          <Route path="/authorize-card/:token" element={<Suspense fallback={<RouteLoader />}><AuthorizeCardPage /></Suspense>} />
          <Route path="/card-terms" element={<Suspense fallback={<RouteLoader />}><CardTermsPage /></Suspense>} />
          {/* Public website routes */}
          <Route element={<Suspense fallback={<RouteLoader />}><WebsiteLayout /></Suspense>}>
            <Route path="/home" element={<Suspense fallback={<RouteLoader />}><CMSPage slug="home" fallback={<HomePage />} /></Suspense>} />
            <Route path="/test-catalog" element={<Suspense fallback={<RouteLoader />}><TestCatalogPage /></Suspense>} />
            <Route path="/contact" element={<Suspense fallback={<RouteLoader />}><ContactPage /></Suspense>} />
            {/* Public legal pages — must be hardcoded so Google OAuth
                verification reviewers can always load them, independent of
                any CMS data state. */}
            <Route path="/privacy" element={<Suspense fallback={<RouteLoader />}><PrivacyPage /></Suspense>} />
            <Route path="/terms"   element={<Suspense fallback={<RouteLoader />}><TermsPage /></Suspense>} />
            <Route path="/page/:slug" element={<Suspense fallback={<RouteLoader />}><CMSPage /></Suspense>} />
            <Route path="/site/:slug" element={<Suspense fallback={<RouteLoader />}><CMSPage /></Suspense>} />
          </Route>
          {/* Patient portal — a separate audience with its own login and
              its own token. It must sit OUTSIDE the staff shell: a patient is
              not a staff user, and routing them through the staff session
              would be the same identity confusion this replaced. */}
          <Route path="/portal" element={<Suspense fallback={<RouteLoader />}><PatientPortal /></Suspense>} />
          {/* External sales reps: their own audience, own token, outside the staff shell. */}
          <Route path="/rep" element={<Suspense fallback={<RouteLoader />}><RepPortal /></Suspense>} />
          {/* Mobile collector, NOT signed in. Rendering the app here would
              show it fetching /api/collect/me without a token and failing
              with a banner; falling through to the catch-all would land a
              collector on the marketing homepage. Send them to the login
              they actually need. */}
          <Route path="/collect" element={<Navigate to="/login" replace />} />
          {/* /kiosk IS NOT DECLARED HERE, and that is the point.
              It used to be, which meant the station rendered for anybody who
              typed the address — no login, and outside RouteGuard, so the
              page permission an administrator had set was never consulted.
              A station that receives specimens is staff software: it belongs
              behind a real session. It is still declared for a signed-in
              user below, outside MainLayout, so it keeps the full viewport. */}
          {/* Public e-signature link — recipient opens this from an email
              and does NOT need to log in. Token gates access. */}
          <Route path="/sign/:token" element={<Suspense fallback={<RouteLoader />}><SigningPage /></Suspense>} />
          <Route path="/w9/:token" element={<Suspense fallback={<RouteLoader />}><W9Page /></Suspense>} />
          {/* The patient's questionnaire. Declared in BOTH route trees:
              a patient has no session, and a member of staff opening the
              link does have one — the same page must answer either way. */}
          <Route path="/questionnaire/:token" element={<Suspense fallback={<RouteLoader />}><PatientQuestionnairePage /></Suspense>} />
          {/* The provider signature link. It was declared only inside the
              signed-in shell, so a provider opening it from their email —
              which is the entire point of it — fell through to the catch-all
              below and landed on the marketing homepage. The token is the
              auth; there is nothing to sign in to. */}
          <Route path="/sign-provider/:token" element={<Suspense fallback={<RouteLoader />}><PublicSignProvider /></Suspense>} />
          {/* A CLIENT-SIDE navigation here rendered the SPA's own stored copy
              of /home instead of fetching the server-rendered site, which is
              how a visitor to a tenant domain could end up looking at another
              tenant's page. nginx now serves the bare domain from the backend,
              so this only fires if the SPA is loaded at / some other way — and
              when it does it must leave the SPA, not route inside it. */}
          <Route path="/" element={<HardRedirect to="/home" />} />
          <Route path="*" element={<Navigate to="/home" replace />} />
        </Routes>
      </Router>
    );
  }

  return oauthWrapper(
    <Router>
      <HelpProvider>
      <TenantTitle />
      <HelpSidebar />
      {/* First-login Theme Picker */}
      {themePicker && (
        <div style={{ position: 'fixed', inset: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100000, background: 'rgba(0,0,0,0.55)', backdropFilter: 'blur(8px)', animation: 'fadeIn 0.3s ease' }}>
          <div style={{
            background: 'var(--card-bg, white)', backdropFilter: 'blur(40px)', borderRadius: 24, padding: '32px 36px',
            maxWidth: 460, width: '90%', textAlign: 'center', border: '1px solid var(--glass-border, rgba(0,0,0,0.08))',
            boxShadow: '0 24px 80px rgba(0,0,0,0.25)', animation: 'popIn 0.4s cubic-bezier(0.34,1.56,0.64,1)',
          }}>
            <div style={{ fontSize: 36, marginBottom: 8 }}>🎨</div>
            <div style={{ fontSize: 22, fontWeight: 800, color: 'var(--text-primary, #1D1D1F)', letterSpacing: '-0.02em', marginBottom: 6 }}>
              Choose your appearance
            </div>
            <div style={{ fontSize: 13, color: 'var(--text-secondary, #6E6E73)', marginBottom: 22 }}>
              Pick how the app should look. You can change this anytime in <strong>My Account → Appearance</strong>.
            </div>
            <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: 14, marginBottom: 8 }}>
              <button onClick={() => pickTheme(false)} style={{
                padding: '22px 16px', borderRadius: 16, cursor: 'pointer',
                background: !dark ? 'rgba(0,122,255,0.08)' : 'var(--hover-bg, rgba(0,0,0,0.03))',
                border: `2px solid ${!dark ? 'var(--accent, #007AFF)' : 'var(--glass-border, rgba(0,0,0,0.08))'}`,
                color: 'var(--text-primary, #1D1D1F)', fontSize: 14, fontWeight: 700,
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, transition: 'transform 0.15s, border-color 0.15s',
              }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
                <span style={{ fontSize: 32 }}>☀️</span>
                <span>Light</span>
                <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-secondary, #6E6E73)' }}>Bright & crisp</span>
              </button>
              <button onClick={() => pickTheme(true)} style={{
                padding: '22px 16px', borderRadius: 16, cursor: 'pointer',
                background: dark ? 'rgba(0,122,255,0.12)' : 'var(--hover-bg, rgba(0,0,0,0.03))',
                border: `2px solid ${dark ? 'var(--accent, #0A84FF)' : 'var(--glass-border, rgba(0,0,0,0.08))'}`,
                color: 'var(--text-primary, #1D1D1F)', fontSize: 14, fontWeight: 700,
                display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 8, transition: 'transform 0.15s, border-color 0.15s',
              }}
                onMouseEnter={e => e.currentTarget.style.transform = 'translateY(-2px)'}
                onMouseLeave={e => e.currentTarget.style.transform = 'translateY(0)'}>
                <span style={{ fontSize: 32 }}>🌙</span>
                <span>Dark</span>
                <span style={{ fontSize: 11, fontWeight: 500, color: 'var(--text-secondary, #6E6E73)' }}>Easy on the eyes</span>
              </button>
            </div>
          </div>
        </div>
      )}
      {/* Welcome Popup */}
      {welcomePopup && (
        <div style={{ position: 'fixed', top: 0, left: 0, right: 0, bottom: 0, display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999, background: 'rgba(0,0,0,0.5)', backdropFilter: 'blur(8px)', animation: 'fadeIn 0.3s ease' }}
          onClick={() => setWelcomePopup(null)}>
          <div onClick={e => e.stopPropagation()} style={{
            background: 'var(--card-bg, white)', backdropFilter: 'blur(40px)', borderRadius: 24, padding: '36px 40px',
            maxWidth: 480, width: '90%', textAlign: 'center', border: '1px solid var(--glass-border, rgba(0,0,0,0.08))',
            boxShadow: '0 24px 80px rgba(0,0,0,0.2)', animation: 'popIn 0.4s cubic-bezier(0.34,1.56,0.64,1)',
          }}>
            <div style={{ fontSize: 40, marginBottom: 12 }}>👋</div>
            <div style={{ fontSize: 26, fontWeight: 800, color: 'var(--text-primary, #1D1D1F)', letterSpacing: '-0.02em', marginBottom: 4 }}>
              {welcomePopup.greeting}, {welcomePopup.name}!
            </div>
            <div style={{ fontSize: 14, color: 'var(--text-secondary, #6E6E73)', marginBottom: 24 }}>Welcome back to Enterprise LIS</div>
            {welcomePopup.setup && (
              <div style={{
                background: 'var(--hover-bg, rgba(0,122,255,0.04))', borderRadius: 16, padding: '20px 24px', marginBottom: 20,
                border: '1px solid var(--glass-border, rgba(0,0,0,0.06))',
              }}>
                <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary, #1D1D1F)', lineHeight: 1.5, marginBottom: 10 }}>
                  {welcomePopup.setup}
                </div>
                <div style={{ fontSize: 15, color: 'var(--accent, #007AFF)', fontWeight: 700, fontStyle: 'italic' }}>
                  {welcomePopup.punchline}
                </div>
              </div>
            )}
            <button onClick={() => setWelcomePopup(null)} style={{
              padding: '10px 32px', border: 'none', borderRadius: 980, background: 'var(--accent, #007AFF)',
              color: 'white', fontSize: 14, fontWeight: 600, cursor: 'pointer', transition: 'transform 0.15s',
            }}
              onMouseEnter={e => e.currentTarget.style.transform = 'scale(1.03)'}
              onMouseLeave={e => e.currentTarget.style.transform = 'scale(1)'}>
              Let's Go!
            </button>
          </div>
          <style>{`
            @keyframes fadeIn { from { opacity: 0; } to { opacity: 1; } }
            @keyframes popIn { from { opacity: 0; transform: scale(0.9) translateY(20px); } to { opacity: 1; transform: scale(1) translateY(0); } }
          `}</style>
        </div>
      )}
      <ScreenShareProvider>
      <ImpersonationBanner />
      {/* The banner is fixed; give it room rather than letting it cover the
          first row of whatever is underneath. */}
      {readImpersonation() ? <div style={{ height: 34 }} /> : null}
      <Routes>
        {/* Public sign link works for logged-in users too — rendered outside
            the MainLayout so the signing page can take the full viewport. */}
        <Route path="/sign/:token" element={<Suspense fallback={<RouteLoader />}><SigningPage /></Suspense>} />
          <Route path="/w9/:token" element={<Suspense fallback={<RouteLoader />}><W9Page /></Suspense>} />
          <Route path="/questionnaire/:token" element={<Suspense fallback={<RouteLoader />}><PatientQuestionnairePage /></Suspense>} />
        {/* Outside MainLayout on purpose: a provider handed a link gets the
            same full-page signing experience whether or not a colleague's
            session happens to be in the browser. */}
        <Route path="/sign-provider/:token" element={<Suspense fallback={<RouteLoader />}><PublicSignProvider /></Suspense>} />
        {/* Kiosk mode is a fullscreen experience — rendered outside MainLayout
            so it takes the full viewport. Signed-in only: the logged-out copy
            of this route was removed, because a drop-off station that books
            in specimens is staff software, not a public page. */}
        <Route path="/kiosk" element={<Suspense fallback={<RouteLoader />}><KioskMode /></Suspense>} />
        {/* THE WALL DISPLAY. Outside MainLayout so it owns the whole
            viewport, and outside RouteGuard because it holds NO USER
            SESSION: it authenticates as a device with a pairing code, and
            the laboratory it shows comes from its own device row rather
            than from the lab picker in a header it does not have.

            That is also why it is not /kiosk — that path is already the
            touch-input specimen check-in station, and the page code is
            taken too. A board is a display; a kiosk takes input. */}
        <Route path="/wallboard" element={<Suspense fallback={<RouteLoader />}><Wallboard /></Suspense>} />
        {/* The drop-off window. Full viewport like the kiosk, and signed-in
            like it too: a station that books specimens in is staff software. */}
        <Route path="/specimen-checkin" element={<Suspense fallback={<RouteLoader />}><SpecimenCheckIn /></Suspense>} />
        <Route path="/rep" element={<Suspense fallback={<RouteLoader />}><RepPortal /></Suspense>} />
        {/* Mobile collector, already signed in. Same component: it carries no
            token of its own, so the staff session it already has is the one
            /api/collect authorises. */}
        {/* THE COLLECTOR APP IS A PHONE IN A CAR PARK.
            A render error here showed a WHITE SCREEN with nothing on it and
            nothing the collector could report — "when i click collect the
            page goes blank". There is no way for them to open a console.
            The boundary turns the same crash into a message and a Try again,
            and prints the error where support can read it back. */}
        <Route path="/collect" element={
          <ErrorBoundary name="Mobile Collector">
            <Suspense fallback={<RouteLoader />}><CollectApp /></Suspense>
          </ErrorBoundary>} />
        {/* /portal for somebody who is ALREADY SIGNED IN.
            Two wrong answers were tried here before this one:

            1. Not declaring it at all. The request fell through to the staff
               shell below and the staff RouteGuard refused it — including for
               role=patient, whose portal it is — and the deny page's only exit
               led straight back. A dead end.
            2. Rendering <PatientPortal/> directly. That component looks for a
               portal token, finds none, and shows its OWN sign-in form. So a
               person who had just signed in was asked to sign in again, on a
               second form, against a different account table. A second dead
               end, and the reason there appeared to be two login pages.

            PatientRoute is the answer that already existed for /patient: it
            trades the session the person is holding for a portal session
            (POST /api/portal/session-from-staff) and then renders the portal
            itself. Nobody signed in is ever asked to sign in again, and the
            portal's own form stays where it belongs — on the signed-OUT route
            below, for people who have a portal account and nothing else. */}
        <Route path="/portal" element={<Suspense fallback={<RouteLoader />}><PatientRoute standalone /></Suspense>} />
        <Route element={<RouteGuard user={user} onLogout={handleLogout}><MainLayout currentUser={user} onLogout={handleLogout} /></RouteGuard>}>
          {/* Dashboard */}
          <Route path="/dashboard" element={
            <Suspense fallback={<RouteLoader />}>
              {role === 'patient' ? <PatientRoute /> : <ExecutiveDashboard />}
            </Suspense>
          } />
          <Route path="/patient" element={<Suspense fallback={<RouteLoader />}><PatientRoute /></Suspense>} />
          <Route path="/lab" element={<Suspense fallback={<RouteLoader />}><LabPortalDashboard /></Suspense>} />

          {/* Core LIS */}
          <Route path="/patients" element={<Suspense fallback={<RouteLoader />}><PatientsPage /></Suspense>} />
          <Route path="/assessments" element={<Suspense fallback={<RouteLoader />}><AssessmentsPage /></Suspense>} />
          <Route path="/patients/duplicates" element={<Suspense fallback={<RouteLoader />}><DuplicatePatientsPage /></Suspense>} />
          <Route path="/patients/access-requests" element={<Suspense fallback={<RouteLoader />}><PortalAccessRequestsPage /></Suspense>} />
          <Route path="/orders" element={<Suspense fallback={<RouteLoader />}><OrdersPage /></Suspense>} />
          <Route path="/mail-queue" element={<Suspense fallback={<RouteLoader />}><MailQueuePage /></Suspense>} />
          <Route path="/call-logs" element={<Suspense fallback={<RouteLoader />}><CallLogsPage /></Suspense>} />
          <Route path="/requisitions" element={<Suspense fallback={<RouteLoader />}><RequisitionsPage /></Suspense>} />
          <Route path="/results" element={<Suspense fallback={<RouteLoader />}><ResultsPage /></Suspense>} />
          <Route path="/patients-manager" element={<Suspense fallback={<RouteLoader />}><PatientsManager /></Suspense>} />
          <Route path="/orders-manager" element={<Suspense fallback={<RouteLoader />}><OrdersManager /></Suspense>} />
          <Route path="/results-manager" element={<Suspense fallback={<RouteLoader />}><ResultsManager /></Suspense>} />
          <Route path="/specimens" element={<Suspense fallback={<RouteLoader />}><SpecimensTracker /></Suspense>} />
          <Route path="/qc" element={<Suspense fallback={<RouteLoader />}><QCManager /></Suspense>} />
          <Route path="/audit-logs" element={<Suspense fallback={<RouteLoader />}><AuditLogsViewer /></Suspense>} />

          {/* Providers */}
          <Route path="/providers" element={<Suspense fallback={<RouteLoader />}><ProvidersManager /></Suspense>} />

          {/* RCM */}
          <Route path="/rcm" element={<Suspense fallback={<RouteLoader />}><RCMDashboard /></Suspense>} />
          <Route path="/rcm/eligibility" element={<Suspense fallback={<RouteLoader />}><RCMModule /></Suspense>} />
          <Route path="/payer-portals" element={<Suspense fallback={<RouteLoader />}><PayerPortalsPage /></Suspense>} />
          <Route path="/rcm/submit-claims" element={<Suspense fallback={<RouteLoader />}><RCMModule /></Suspense>} />
          {/* Paper claims is handled by the new PaperClaimsQueue route below */}
          <Route path="/rcm/eob-claims" element={<Suspense fallback={<RouteLoader />}><EOBManager /></Suspense>} />
          <Route path="/rcm/eob" element={<Suspense fallback={<RouteLoader />}><EOBManager /></Suspense>} />
          {/* Money a payer paid and later took back — by demand letter, or
              netted out of a later cheque. */}
          <Route path="/rcm/recoupments" element={<Suspense fallback={<RouteLoader />}><RecoupmentsManager /></Suspense>} />
          <Route path="/rcm/refunds" element={<Navigate to="/rcm/recoupments" replace />} />
          <Route path="/fax" element={<Suspense fallback={<RouteLoader />}><FaxInboxPage /></Suspense>} />
          <Route path="/fax-inbox" element={<Suspense fallback={<RouteLoader />}><FaxInboxPage /></Suspense>} />
          <Route path="/email" element={<Suspense fallback={<RouteLoader />}><EmailInboxPage /></Suspense>} />
          <Route path="/rcm/denials" element={<Suspense fallback={<RouteLoader />}><RCMModule /></Suspense>} />
          <Route path="/rcm/actions" element={<Suspense fallback={<RouteLoader />}><RCMModule /></Suspense>} />
          <Route path="/rcm/era-posting" element={<Suspense fallback={<RouteLoader />}><RCMModule /></Suspense>} />
          <Route path="/rcm/cost-estimator" element={<Suspense fallback={<RouteLoader />}><RCMModule /></Suspense>} />
          <Route path="/rcm/payer-intelligence" element={<Suspense fallback={<RouteLoader />}><RCMModule /></Suspense>} />
          <Route path="/rcm/risk-assessment" element={<Suspense fallback={<RouteLoader />}><RCMModule /></Suspense>} />
          <Route path="/rcm/credentialing" element={<Suspense fallback={<RouteLoader />}><RCMModule /></Suspense>} />
          <Route path="/rcm/provider-analytics" element={<Suspense fallback={<RouteLoader />}><RCMModule /></Suspense>} />
          <Route path="/rcm/leaderboard" element={<Suspense fallback={<RouteLoader />}><RCMModule /></Suspense>} />
          {/* /rcm/patient-statements removed — superseded by /statements
              (StatementsManager). Redirect preserves any bookmarked URLs. */}
          <Route path="/rcm/patient-statements" element={<Navigate to="/statements" replace />} />
          <Route path="/rcm/deadlines" element={<Suspense fallback={<RouteLoader />}><RCMModule /></Suspense>} />
          <Route path="/rcm/revenue-forecast" element={<Suspense fallback={<RouteLoader />}><RCMModule /></Suspense>} />
          <Route path="/rcm/smart-tasks" element={<Suspense fallback={<RouteLoader />}><RCMModule /></Suspense>} />
          <Route path="/rcm/ml-prediction" element={<Suspense fallback={<RouteLoader />}><RCMModule /></Suspense>} />
          <Route path="/rcm/auto-verify" element={<Suspense fallback={<RouteLoader />}><RCMModule /></Suspense>} />
          <Route path="/rcm/templates" element={<Suspense fallback={<RouteLoader />}><RCMModule /></Suspense>} />
          <Route path="/rcm/bulk-appeals" element={<Suspense fallback={<RouteLoader />}><RCMModule /></Suspense>} />
          <Route path="/rcm/payment-posting" element={<Suspense fallback={<RouteLoader />}><RCMModule /></Suspense>} />
          <Route path="/rcm/patient-financials" element={<Suspense fallback={<RouteLoader />}><RCMModule /></Suspense>} />
          <Route path="/rcm/client-financials" element={<Suspense fallback={<RouteLoader />}><RCMModule /></Suspense>} />
          <Route path="/claims" element={<Suspense fallback={<RouteLoader />}><ClaimsManager /></Suspense>} />
          <Route path="/rcm/paper-claims" element={<Suspense fallback={<RouteLoader />}><PaperClaimsQueue /></Suspense>} />
          <Route path="/rcm/payers" element={<Suspense fallback={<RouteLoader />}><PayersPage /></Suspense>} />
          <Route path="/billing/payers" element={<Suspense fallback={<RouteLoader />}><PayersPage /></Suspense>} />
          <Route path="/rcm/work-queues" element={<Suspense fallback={<RouteLoader />}><WorkQueuesPage /></Suspense>} />
          <Route path="/rcm/appeals" element={<Suspense fallback={<RouteLoader />}><AppealsPage /></Suspense>} />
          <Route path="/billing/work-queues" element={<Suspense fallback={<RouteLoader />}><WorkQueuesPage /></Suspense>} />
          <Route path="/settings/billing-ai" element={<Suspense fallback={<RouteLoader />}><AISettingsPage /></Suspense>} />
          <Route path="/billing/ai-settings" element={<Suspense fallback={<RouteLoader />}><AISettingsPage /></Suspense>} />
          <Route path="/invoices" element={<Suspense fallback={<RouteLoader />}><InvoicesManager /></Suspense>} />
          <Route path="/statements" element={<Suspense fallback={<RouteLoader />}><StatementsManager /></Suspense>} />
          {/* Insurance management is in Settings > Insurances */}

          {/* Inventory */}
          <Route path="/inventory" element={<Suspense fallback={<RouteLoader />}><InventoryDashboard /></Suspense>} />
          <Route path="/inventory/items" element={<Suspense fallback={<RouteLoader />}><InventoryItems /></Suspense>} />
          <Route path="/inventory/lots" element={<Suspense fallback={<RouteLoader />}><InventoryLots /></Suspense>} />
          <Route path="/inventory/po" element={<Suspense fallback={<RouteLoader />}><PurchaseOrders /></Suspense>} />
          <Route path="/inventory/alerts" element={<Suspense fallback={<RouteLoader />}><ReorderAlerts /></Suspense>} />
          <Route path="/inventory/hub" element={<Suspense fallback={<RouteLoader />}><InventoryHub /></Suspense>} />
          <Route path="/inventory/audit" element={<Suspense fallback={<RouteLoader />}><InventoryAudit /></Suspense>} />
          <Route path="/inventory/supply-requests" element={<Suspense fallback={<RouteLoader />}><SupplyRequestsPage /></Suspense>} />
          <Route path="/inventory/settings" element={<Suspense fallback={<RouteLoader />}><InventorySettings /></Suspense>} />
          <Route path="/consumption" element={<Suspense fallback={<RouteLoader />}><ConsumptionStation /></Suspense>} />
          <Route path="/executive" element={<Suspense fallback={<RouteLoader />}><ExecutivePage /></Suspense>} />
          <Route path="/user-activity" element={<Suspense fallback={<RouteLoader />}><UserActivityPage /></Suspense>} />
          <Route path="/purchasing" element={<Suspense fallback={<RouteLoader />}><PurchasingPage /></Suspense>} />
          <Route path="/purchasing/orders/new" element={<Suspense fallback={<RouteLoader />}><PurchaseOrderDetailPage /></Suspense>} />
          <Route path="/purchasing/orders/:poId" element={<Suspense fallback={<RouteLoader />}><PurchaseOrderDetailPage /></Suspense>} />
          {/* /warehouse removed — Warehouses now lives in Inventory Settings */}
          <Route path="/fulfillment" element={<Suspense fallback={<RouteLoader />}><FulfillmentManager /></Suspense>} />

          {/* E-Commerce */}
          <Route path="/store" element={<Suspense fallback={<RouteLoader />}><StoreFront /></Suspense>} />
          <Route path="/cart" element={<Suspense fallback={<RouteLoader />}><CartPage /></Suspense>} />
          <Route path="/checkout" element={<Suspense fallback={<RouteLoader />}><CheckoutPage /></Suspense>} />
          <Route path="/order-confirmation" element={<Suspense fallback={<RouteLoader />}><OrderConfirmation /></Suspense>} />
          <Route path="/my-orders" element={<Suspense fallback={<RouteLoader />}><MyOrders /></Suspense>} />
          <Route path="/my-subscriptions" element={<Suspense fallback={<RouteLoader />}><MySubscriptions /></Suspense>} />
          <Route path="/admin/ecommerce" element={<Suspense fallback={<RouteLoader />}><AdminEcommerceDashboard /></Suspense>} />
          <Route path="/admin/fulfillment" element={<Suspense fallback={<RouteLoader />}><AdminFulfillment /></Suspense>} />
          <Route path="/admin/shipments" element={<Suspense fallback={<RouteLoader />}><AdminShipments /></Suspense>} />
          <Route path="/admin/subscriptions" element={<Suspense fallback={<RouteLoader />}><SubscriptionManager /></Suspense>} />

          {/* Config */}
          {/* Legacy URLs → Settings. The standalone TestsManager /
              AnalytesManager / PanelsManager pages under components/lis/
              were superseded by the full-featured versions under
              components/settings/; these redirects keep old bookmarks
              working. */}
          <Route path="/tests"    element={<Navigate to="/settings?section=tests"    replace />} />
          <Route path="/analytes" element={<Navigate to="/settings?section=analytes" replace />} />
          <Route path="/panels"   element={<Navigate to="/settings?section=panels"   replace />} />
          <Route path="/business-rules" element={<Suspense fallback={<RouteLoader />}><BusinessLogicRules /></Suspense>} />

          {/* Operations & Admin */}
          <Route path="/operations" element={<Suspense fallback={<RouteLoader />}><OperationsDashboard /></Suspense>} />
          <Route path="/ops" element={<Suspense fallback={<RouteLoader />}><LabOpsHub /></Suspense>} />
          <Route path="/phlebotomy-log" element={<Suspense fallback={<RouteLoader />}><PhlebotomyLog /></Suspense>} />
          <Route path="/eod-report" element={<Suspense fallback={<RouteLoader />}><EodReport /></Suspense>} />
          <Route path="/eod-reports" element={<Suspense fallback={<RouteLoader />}><EodReportsAdmin /></Suspense>} />
          <Route path="/chat" element={<Suspense fallback={<RouteLoader />}><DepartmentChat /></Suspense>} />
          <Route path="/messaging" element={<Suspense fallback={<RouteLoader />}><MessagingDashboard /></Suspense>} />
          <Route path="/tech-route/:techId" element={<Suspense fallback={<RouteLoader />}><TechRoutePage /></Suspense>} />
          <Route path="/schedule" element={<Suspense fallback={<RouteLoader />}><SchedulePage /></Suspense>} />
          <Route path="/clients" element={<Suspense fallback={<RouteLoader />}><ClientManagementPage /></Suspense>} />
          <Route path="/user-settings" element={<Suspense fallback={<RouteLoader />}><UserSettings /></Suspense>} />
          <Route path="/my-account" element={<Suspense fallback={<RouteLoader />}><MyAccountPage /></Suspense>} />
          <Route path="/compliance/oig" element={<Suspense fallback={<RouteLoader />}><OIGScreening /></Suspense>} />
          <Route path="/commissions" element={<Suspense fallback={<RouteLoader />}><CommissionsPage /></Suspense>} />
          {/* One rep, at their own address. The panel fills the screen, so it
              behaves like a page and should be linkable like one. */}
          <Route path="/commissions/reps/:repId" element={<Suspense fallback={<RouteLoader />}><CommissionsPage /></Suspense>} />
          <Route path="/settings" element={<Suspense fallback={<RouteLoader />}><SettingsPage /></Suspense>} />
          <Route path="/saas-billing" element={<Suspense fallback={<RouteLoader />}><SaasBillingPage /></Suspense>} />
          <Route path="/admin/roles" element={<Suspense fallback={<RouteLoader />}><RoleManagement /></Suspense>} />
          <Route path="/admin/system-health" element={<Suspense fallback={<RouteLoader />}><SystemHealth /></Suspense>} />

          {/* Feature pages */}
          <Route path="/documents" element={<Suspense fallback={<RouteLoader />}><DocumentManagement /></Suspense>} />
          <Route path="/vehicles" element={<Suspense fallback={<RouteLoader />}><VehicleManagement /></Suspense>} />
          {/* Unified Dispatch & Pickups page — replaces three previously-
              separate pages. Legacy paths below redirect into the right tab
              so deep-links from documentation / bookmarks still work. */}
          <Route path="/dispatch-pickups" element={<Suspense fallback={<RouteLoader />}><DispatchPickups /></Suspense>} />
          <Route path="/dispatch" element={<Navigate to="/dispatch-pickups?tab=queue" replace />} />
          <Route path="/field-visits" element={<Navigate to="/dispatch-pickups?tab=history" replace />} />
          <Route path="/visit-logs" element={<Navigate to="/dispatch-pickups?tab=history" replace />} />
          <Route path="/field-schedule" element={<Navigate to="/dispatch-pickups?tab=live" replace />} />
          <Route path="/recurring-schedule" element={<Navigate to="/dispatch-pickups?tab=recurring" replace />} />
          <Route path="/technician-app" element={<Suspense fallback={<RouteLoader />}><TechnicianApp /></Suspense>} />
          <Route path="/lab-maintenance" element={<Suspense fallback={<RouteLoader />}><LabMaintenance /></Suspense>} />
          <Route path="/interfaces" element={<Suspense fallback={<RouteLoader />}><InterfacesPage /></Suspense>} />
          <Route path="/microbiology" element={<Suspense fallback={<RouteLoader />}><MicrobiologyPage /></Suspense>} />
          <Route path="/sendouts" element={<Suspense fallback={<RouteLoader />}><SendOutsPage /></Suspense>} />
          <Route path="/help" element={<Suspense fallback={<RouteLoader />}><HelpCenter /></Suspense>} />
          <Route path="/communication-center" element={<Suspense fallback={<RouteLoader />}><CommunicationCenter /></Suspense>} />
          <Route path="/clients/onboard" element={<Suspense fallback={<RouteLoader />}><OnboardClientWizard /></Suspense>} />
          <Route path="/clients/onboard/:leadId" element={<Suspense fallback={<RouteLoader />}><OnboardClientWizard /></Suspense>} />
          <Route path="/dispatch-pickups/analytics" element={<Suspense fallback={<RouteLoader />}><DispatchAnalytics /></Suspense>} />
          <Route path="/collectors/performance" element={<Suspense fallback={<RouteLoader />}><CollectorPerformance /></Suspense>} />
          <Route path="/rcm/settlements" element={<Suspense fallback={<RouteLoader />}><SettlementsPage /></Suspense>} />
          <Route path="/client-service" element={<Suspense fallback={<RouteLoader />}><ClientServiceDashboard /></Suspense>} />
          <Route path="/google-workspace" element={<Suspense fallback={<RouteLoader />}><GoogleWorkspacePage /></Suspense>} />
          {/* /temperature-logs route removed — superseded by /lab-maintenance Temperature Logs tab. */}
          <Route path="/sales" element={<Suspense fallback={<RouteLoader />}><SalesCRM /></Suspense>} />
          <Route path="/ai-dashboard" element={<Suspense fallback={<RouteLoader />}><AIDashboard /></Suspense>} />
          <Route path="/live-map" element={<Suspense fallback={<RouteLoader />}><LiveMap /></Suspense>} />
          <Route path="/fleet-telematics" element={<Suspense fallback={<RouteLoader />}><FleetTelematics /></Suspense>} />
          <Route path="/fleet-analytics" element={<Suspense fallback={<RouteLoader />}><FleetAnalytics /></Suspense>} />
          <Route path="/accessioning" element={<Suspense fallback={<RouteLoader />}><Accessioning /></Suspense>} />
          <Route path="/lab/order-received" element={<Suspense fallback={<RouteLoader />}><OrderReceiving /></Suspense>} />
          <Route path="/proficiency" element={<Suspense fallback={<RouteLoader />}><ProficiencyTesting /></Suspense>} />
          <Route path="/tasks" element={<Suspense fallback={<RouteLoader />}><TasksPage /></Suspense>} />
          <Route path="/accessioning/manager" element={<Suspense fallback={<RouteLoader />}><AccessioningDashboard /></Suspense>} />
          <Route path="/billing/manager" element={<Suspense fallback={<RouteLoader />}><BillingManagerDashboard /></Suspense>} />
          <Route path="/billing/biller" element={<Suspense fallback={<RouteLoader />}><BillerDashboard /></Suspense>} />
          <Route path="/rcm/fee-schedules" element={<Suspense fallback={<RouteLoader />}><FeeSchedulesPage /></Suspense>} />
          <Route path="/tech-app" element={<Suspense fallback={<RouteLoader />}><MobileTechView /></Suspense>} />
          <Route path="/report-builder" element={<Suspense fallback={<RouteLoader />}><ReportBuilder /></Suspense>} />

          {/* CMS */}
          <Route path="/pdf-editor" element={<Suspense fallback={<RouteLoader />}><PDFHub /></Suspense>} />
          <Route path="/cms" element={<Suspense fallback={<RouteLoader />}><CMSManager /></Suspense>} />
          <Route path="/cms/builder/:pageId" element={<Suspense fallback={<RouteLoader />}><PageBuilder /></Suspense>} />

          {/* Anything unrecognised goes HOME — and home is whatever this
              person can actually open, not a hardcoded /dashboard. See
              HomeRedirect. */}
          <Route path="*" element={<HomeRedirect user={user} />} />
        </Route>

        {/* Public website — rendered without sidebar/header */}
        <Route element={<Suspense fallback={<RouteLoader />}><WebsiteLayout /></Suspense>}>
          <Route path="/home" element={<Suspense fallback={<RouteLoader />}><CMSPage slug="home" fallback={<HomePage />} /></Suspense>} />
          <Route path="/test-catalog" element={<Suspense fallback={<RouteLoader />}><TestCatalogPage /></Suspense>} />
          <Route path="/contact" element={<Suspense fallback={<RouteLoader />}><ContactPage /></Suspense>} />
          <Route path="/privacy" element={<Suspense fallback={<RouteLoader />}><PrivacyPage /></Suspense>} />
          <Route path="/terms"   element={<Suspense fallback={<RouteLoader />}><TermsPage /></Suspense>} />
          <Route path="/page/:slug" element={<Suspense fallback={<RouteLoader />}><CMSPage /></Suspense>} />
          <Route path="/site/:slug" element={<Suspense fallback={<RouteLoader />}><CMSPage /></Suspense>} />
        </Route>
      </Routes>
      </ScreenShareProvider>
      </HelpProvider>
    </Router>
  );
}

const CallbackReminderWatcher = React.lazy(() => import('./components/lis/CallbackReminderWatcher'));
const CallConfirmReminderWatcher = React.lazy(() => import('./components/lis/CallConfirmReminderWatcher'));

function AppWithTheme() {
  return (
    <ThemeProvider>
      <App />
      <React.Suspense fallback={null}>
        <CallbackReminderWatcher />
        <CallConfirmReminderWatcher />
      </React.Suspense>
    </ThemeProvider>
  );
}

export default AppWithTheme;
